/**
 * `/auth/request` — captain enters their email or iMessage handle, we
 * POST it to `/api/auth/request`, the Worker emails a 6-digit OTP.
 *
 * The handle field accepts either an email or an iMessage handle (phone
 * or email format). We don't try to disambiguate client-side — the
 * Worker's captain lookup matches against both columns. Anti-enumeration
 * lives on the server (every miss returns a 200-shaped success body for
 * the unauth case, but we still POST and read whatever comes back).
 */

import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError, apiPost } from "../lib/api";

type SubmitState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "sent"; handle: string }
  | { kind: "error"; message: string };

export default function AuthRequest() {
  const [handle, setHandle] = useState("");
  const [state, setState] = useState<SubmitState>({ kind: "idle" });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = handle.trim();
    if (trimmed.length < 3) {
      setState({
        kind: "error",
        message: "Enter your email or iMessage handle.",
      });
      return;
    }
    setState({ kind: "loading" });
    try {
      await apiPost<{ ok: boolean }>("/api/auth/request", { handle: trimmed });
      setState({ kind: "sent", handle: trimmed });
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? "We couldn't send your code right now. Try again in a minute."
          : "Network error. Check your connection and retry.";
      setState({ kind: "error", message: msg });
    }
  }

  if (state.kind === "sent") {
    const verifyHref = `/auth/verify?handle=${encodeURIComponent(state.handle)}`;
    return (
      <section>
        <h1>Check your email</h1>
        <p>
          We sent a 6-digit code to <code>{state.handle}</code>. It expires in
          10 minutes.
        </p>
        <p>
          <Link className="button" to={verifyHref}>
            Enter code
          </Link>
        </p>
      </section>
    );
  }

  return (
    <section>
      <h1>Sign in</h1>
      <p>Enter your captain email or iMessage handle.</p>
      <form onSubmit={onSubmit} noValidate>
        <p>
          <label htmlFor="handle">
            <span>Email or iMessage handle</span>
          </label>
          <br />
          <input
            id="handle"
            name="handle"
            type="text"
            autoComplete="email"
            autoFocus
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            disabled={state.kind === "loading"}
            style={{ width: "100%", maxWidth: 360, padding: "0.5rem" }}
          />
        </p>
        <p>
          <button
            type="submit"
            className="button"
            disabled={state.kind === "loading"}
          >
            {state.kind === "loading" ? "Sending…" : "Send code"}
          </button>
        </p>
        {state.kind === "error" && (
          <p role="alert" style={{ color: "#b3261e" }}>
            {state.message}
          </p>
        )}
      </form>
    </section>
  );
}
